import React, { useState, useEffect, useCallback, useContext } from "react";
import { makeStyles, Grid, Paper, Typography, Box, CircularProgress, useTheme, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Popover } from "@material-ui/core";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import api from "../../services/api";
import SafeReactApexChart from "../Dashboard/SafeReactApexChart";
import { sanitizeApexOptions } from "../Dashboard/sanitizeApexOptions";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import UpdateSystemProgress from "./UpdateSystemProgress";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper
  },
  statCard: {
    padding: theme.spacing(2),
    textAlign: "center",
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    minHeight: 100,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center"
  },
  statValue: {
    fontSize: "2rem",
    fontWeight: "bold",
    marginTop: theme.spacing(1)
  },
  statLabel: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase"
  },
  chartContainer: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  table: {
    minWidth: 650
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 400
  }
}));

const ServerDashboard = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const [processes, setProcesses] = useState([]);
  const [loadingProcesses, setLoadingProcesses] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [pm2Logs, setPm2Logs] = useState([]);
  const [loadingPm2Logs, setLoadingPm2Logs] = useState(false);
  const [autoRefreshLogs, setAutoRefreshLogs] = useState(false);
  const [commandDialog, setCommandDialog] = useState({ open: false, type: null, confirmation: "" });
  const [executingCommand, setExecutingCommand] = useState(false);
  const [helpAnchorEl, setHelpAnchorEl] = useState({ restartPm2: null, restartServer: null, updateServer: null, updateSystem: null });
  const [updateSystemProgress, setUpdateSystemProgress] = useState({ open: false, logs: [], progress: 0, status: "running" });
  const [rootPassword, setRootPassword] = useState("");
  const [rootPasswordConfigured, setRootPasswordConfigured] = useState(false);
  const [savingRootPassword, setSavingRootPassword] = useState(false);

  const formatPhoneNumber = (number) => {
    if (!number) return "-";
    
    // Remove caracteres não numéricos
    const cleaned = number.replace(/\D/g, "");
    
    // Se for número brasileiro (12 ou 13 dígitos com código 55)
    if (cleaned.length === 12 && cleaned.startsWith('55')) {
      const ddd = cleaned.substring(2, 4);
      const firstPart = cleaned.substring(4, 9);
      const lastPart = cleaned.substring(9);
      return `+55 (${ddd}) ${firstPart}-${lastPart}`;
    }
    
    if (cleaned.length === 13 && cleaned.startsWith('55')) {
      const ddd = cleaned.substring(2, 4);
      const firstPart = cleaned.substring(4, 9);
      const lastPart = cleaned.substring(9);
      return `+55 (${ddd}) ${firstPart}-${lastPart}`;
    }
    
    // Se for muito longo, pode ser grupo
    if (number.length > 15) {
      return `Grupo (${number.substring(0, 10)}...)`;
    }
    
    return number;
  };

  useEffect(() => {
    loadStats();
    loadRootPasswordStatus();
    // Atualizar a cada 30 segundos
    const interval = setInterval(loadStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadRootPasswordStatus = async () => {
    try {
      const { data } = await api.get("/root-password");
      setRootPasswordConfigured(data.data?.configured || false);
    } catch (error) {
      console.error("Erro ao verificar status da senha root:", error);
    }
  };

  const handleSaveRootPassword = async () => {
    if (!rootPassword || rootPassword.trim() === "") {
      toast.error("Por favor, digite a senha root");
      return;
    }

    try {
      setSavingRootPassword(true);
      await api.post("/root-password", { password: rootPassword });
      toast.success("Senha root salva com sucesso!");
      setRootPassword("");
      setRootPasswordConfigured(true);
    } catch (error) {
      console.error("Erro ao salvar senha root:", error);
      toast.error(error.response?.data?.message || "Erro ao salvar senha root");
    } finally {
      setSavingRootPassword(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await api.get("/server-stats");
      setStats(data.data);
      setLoading(false);
    } catch (error) {
      console.error("Erro ao carregar estatísticas:", error);
      toast.error("Erro ao carregar estatísticas do servidor");
      setLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const loadProcesses = useCallback(async () => {
    try {
      setLoadingProcesses(true);
      const { data } = await api.get("/server-processes?limit=50");
      setProcesses(data.data || []);
    } catch (error) {
      console.error("Erro ao carregar processos:", error);
      toast.error("Erro ao carregar processos do servidor");
    } finally {
      setLoadingProcesses(false);
    }
  }, []);

  const loadPm2Logs = useCallback(async () => {
    try {
      setLoadingPm2Logs(true);
      const { data } = await api.get("/pm2-logs?lines=200");
      setPm2Logs(data.data?.logs || []);
    } catch (error) {
      console.error("Erro ao carregar logs do PM2:", error);
      toast.error("Erro ao carregar logs do PM2");
    } finally {
      setLoadingPm2Logs(false);
    }
  }, []);

  useEffect(() => {
    if (autoRefresh && currentTab === 1) {
      loadProcesses();
      const interval = setInterval(() => {
        loadProcesses();
      }, 2000); // Atualizar a cada 2 segundos
      return () => clearInterval(interval);
    }
  }, [autoRefresh, currentTab, loadProcesses]);

  useEffect(() => {
    if (autoRefreshLogs && currentTab === 2) {
      loadPm2Logs();
      const interval = setInterval(() => {
        loadPm2Logs();
      }, 3000); // Atualizar a cada 3 segundos
      return () => clearInterval(interval);
    }
  }, [autoRefreshLogs, currentTab, loadPm2Logs]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    if (newValue === 1) {
      // Aba de processos - carregar dados
      loadProcesses();
    } else if (newValue === 2) {
      // Aba de PM2 logs - carregar dados
      loadPm2Logs();
    }
  };

  const handleExecuteCommand = (commandType) => {
    setCommandDialog({
      open: true,
      type: commandType,
      confirmation: ""
    });
  };

  // Função para simular atualização (apenas para teste local)
  const handleSimulateUpdate = async () => {
    try {
      const { data } = await api.post("/update-system/simulate");
      
      if (data.status === "success") {
        // Abrir modal de progresso
        setUpdateSystemProgress(prev => ({ ...prev, open: true }));
        toast.info("🧪 Simulação de atualização iniciada. Acompanhe o progresso no modal.");
      } else {
        toast.error(data.message || "Erro ao iniciar simulação");
      }
    } catch (error) {
      console.error("Erro ao iniciar simulação:", error);
      toast.error(error.response?.data?.message || "Erro ao iniciar simulação");
    }
  };

  const handleHelpClick = (commandType) => (event) => {
    setHelpAnchorEl(prev => ({ ...prev, [commandType]: event.currentTarget }));
  };

  const handleHelpClose = (commandType) => {
    setHelpAnchorEl(prev => ({ ...prev, [commandType]: null }));
  };

  const handleConfirmCommand = async () => {
    const { type, confirmation } = commandDialog;
    
    let command = "";
    let requiresSudo = false;
    let expectedConfirmation = "";

    switch (type) {
      case "restart-pm2":
        command = "su deploy && pm2 restart all";
        expectedConfirmation = "REINICIAR PM2";
        break;
      case "restart-server":
        command = "reboot";
        requiresSudo = true;
        expectedConfirmation = "REINICIAR SERVIDOR";
        break;
      case "update-server":
        command = "apt update -y && apt upgrade -y";
        requiresSudo = true;
        expectedConfirmation = "ATUALIZAR";
        break;
      case "update-system":
        command = "atualizar-sistema";
        requiresSudo = true;
        expectedConfirmation = "ATUALIZAR SISTEMA";
        break;
      default:
        return;
    }

    // Verificar confirmação
    if (confirmation.toUpperCase() !== expectedConfirmation) {
      toast.error(`Confirmação incorreta. Digite: ${expectedConfirmation}`);
      return;
    }

    try {
      setExecutingCommand(true);
      
      // ✅ Tratamento especial para atualizar-sistema: usar endpoint específico com logs em tempo real
      if (type === "update-system") {
        try {
          const { data } = await api.post("/update-system", {
            confirmation: expectedConfirmation
          });
          
          if (data.status === "success") {
            // Abrir modal de progresso
            setUpdateSystemProgress(prev => ({ ...prev, open: true }));
            setCommandDialog({ open: false, type: null, confirmation: "" });
            toast.info("Atualização do sistema iniciada. Acompanhe o progresso no modal.");
          } else {
            toast.error(data.message || "Erro ao iniciar atualização do sistema");
          }
        } catch (error) {
          console.error("Erro ao iniciar atualização do sistema:", error);
          toast.error(error.response?.data?.message || "Erro ao iniciar atualização do sistema");
        } finally {
          setExecutingCommand(false);
        }
        return;
      }
      
      // Outros comandos usam o endpoint padrão
      const { data } = await api.post("/execute-command", {
        command,
        requiresSudo,
        confirmation: expectedConfirmation
      });

      if (data.status === "success") {
        // ✅ Tratamento especial para comandos que podem não retornar resposta imediata
        if (type === "restart-server") {
          toast.warning("Comando de reinicialização executado. O servidor está reiniciando...", {
            autoClose: 10000
          });
          toast.info("O sistema ficará indisponível por 1-2 minutos até o servidor reiniciar completamente.", {
            autoClose: 15000
          });
        } else if (type === "update-server") {
          toast.success("Comando de atualização executado com sucesso!");
          toast.info("Recomendado reiniciar o servidor após a atualização para aplicar todas as mudanças.", {
            autoClose: 10000
          });
        } else {
          toast.success("Comando executado com sucesso!");
        }
        
        setCommandDialog({ open: false, type: null, confirmation: "" });
      } else {
        toast.error(data.data?.error || "Erro ao executar comando");
      }
    } catch (error) {
      console.error("Erro ao executar comando:", error);
      
      // ✅ Tratamento especial para reboot: timeout pode ser esperado (servidor reinicia)
      if (type === "restart-server") {
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || error.code === 'ERR_NETWORK') {
          toast.warning("Comando de reinicialização enviado. O servidor está reiniciando...", {
            autoClose: 10000
          });
          toast.info("O sistema ficará indisponível por 1-2 minutos até o servidor reiniciar completamente.", {
            autoClose: 15000
          });
          setCommandDialog({ open: false, type: null, confirmation: "" });
          setExecutingCommand(false);
          return; // Não tratar como erro para reboot
        }
      }
      
      // Tratar erros de CORS/Network especificamente
      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error') || error.message?.includes('CORS')) {
        toast.error("Erro de conexão. Verifique se a origem do frontend está configurada no backend (FRONTEND_URL ou ALLOWED_ORIGINS)");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error(error.message || "Erro ao executar comando");
      }
    } finally {
      setExecutingCommand(false);
    }
  };

  if (loading) {
    return (
      <Box className={classes.loading}>
        <CircularProgress />
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box className={classes.loading}>
        <Typography>Nenhum dado disponível</Typography>
      </Box>
    );
  }

  // Gráfico de Gauge para Memória (semicircular)
  const memoryGaugeOptions = {
    chart: {
      type: "radialBar",
      height: 280,
      offsetY: 0
    },
    plotOptions: {
      radialBar: {
        startAngle: -90,
        endAngle: 90,
        hollow: {
          size: "70%",
          margin: 0,
          background: "transparent"
        },
        track: {
          show: true,
          background: theme.palette.type === 'dark' ? theme.palette.grey[800] : "#e7e7e7",
          strokeWidth: "97%",
          backgroundOpacity: 1,
          margin: 5
        },
        dataLabels: {
          name: {
            show: true,
            fontSize: "14px",
            fontWeight: 600,
            offsetY: -20,
            color: theme.palette.type === 'dark' ? theme.palette.text.secondary : "#666"
          },
          value: {
            show: true,
            fontSize: "32px",
            fontWeight: 700,
            offsetY: 10,
            formatter: (val) => `${val.toFixed(1)}%`,
            color: theme.palette.type === 'dark' ? theme.palette.text.primary : "#333"
          }
        }
      }
    },
    labels: ["Uso de Memória"],
    colors: [
      stats.server.memory.usedPercent > 80
        ? "#f44336"
        : stats.server.memory.usedPercent > 60
        ? "#ff9800"
        : "#4caf50"
    ],
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "horizontal",
        shadeIntensity: 0.5,
        gradientToColors: [
          stats.server.memory.usedPercent > 80
            ? "#d32f2f"
            : stats.server.memory.usedPercent > 60
            ? "#f57c00"
            : "#388e3c"
        ],
        inverseColors: true,
        opacityFrom: 1,
        opacityTo: 1,
        stops: [0, 100]
      }
    },
    stroke: {
      lineCap: "round"
    }
  };

  // Garantir que usedPercent seja um número válido entre 0 e 100
  const memoryUsedPercent = stats?.server?.memory?.usedPercent != null 
    ? Math.max(0, Math.min(100, parseFloat(stats.server.memory.usedPercent) || 0))
    : 0;
  const memoryGaugeSeries = [memoryUsedPercent];

  // Gráfico de Gauge para CPU (semicircular)
  const cpuGaugeOptions = {
    chart: {
      type: "radialBar",
      height: 280,
      offsetY: 0
    },
    plotOptions: {
      radialBar: {
        startAngle: -90,
        endAngle: 90,
        hollow: {
          size: "70%",
          margin: 0,
          background: "transparent"
        },
        track: {
          show: true,
          background: theme.palette.type === 'dark' ? theme.palette.grey[800] : "#e7e7e7",
          strokeWidth: "97%",
          backgroundOpacity: 1,
          margin: 5
        },
        dataLabels: {
          name: {
            show: true,
            fontSize: "14px",
            fontWeight: 600,
            offsetY: -20,
            color: theme.palette.type === 'dark' ? theme.palette.text.secondary : "#666"
          },
          value: {
            show: true,
            fontSize: "32px",
            fontWeight: 700,
            offsetY: 10,
            formatter: (val) => `${val.toFixed(2)}%`,
            color: theme.palette.type === 'dark' ? theme.palette.text.primary : "#333"
          }
        }
      }
    },
    labels: ["Uso de CPU"],
    colors: [
      stats.server.cpu.usage > 80
        ? "#f44336"
        : stats.server.cpu.usage > 60
        ? "#ff9800"
        : "#4caf50"
    ],
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "horizontal",
        shadeIntensity: 0.5,
        gradientToColors: [
          stats.server.cpu.usage > 80
            ? "#d32f2f"
            : stats.server.cpu.usage > 60
            ? "#f57c00"
            : "#388e3c"
        ],
        inverseColors: true,
        opacityFrom: 1,
        opacityTo: 1,
        stops: [0, 100]
      }
    },
    stroke: {
      lineCap: "round"
    }
  };

  // Garantir que cpu.usage seja um número válido entre 0 e 100
  const cpuUsage = stats?.server?.cpu?.usage != null 
    ? Math.max(0, Math.min(100, parseFloat(stats.server.cpu.usage) || 0))
    : 0;
  const cpuGaugeSeries = [cpuUsage];

  // Gráfico de linha para histórico de CPU
  // ✅ CORRIGIDO: Validar se cpuHistory existe e tem dados
  const cpuHistoryData = stats.server.cpuHistory || [];
  const hasCpuHistory = cpuHistoryData.length > 0;
  
  const cpuHistoryOptions = {
    chart: {
      type: "line",
      height: 350,
      toolbar: {
        show: true
      },
      zoom: {
        enabled: true
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: "smooth",
      width: 2
    },
    xaxis: {
      categories: hasCpuHistory 
        ? cpuHistoryData.map((h) => h.time)
        : ["Aguardando dados..."],
      title: {
        text: "Horário",
        style: {
          color: theme.palette.text.primary
        }
      },
      labels: {
        style: {
          colors: theme.palette.text.secondary
        }
      }
    },
    yaxis: {
      title: {
        text: "Uso de CPU (%)",
        style: {
          color: theme.palette.text.primary
        }
      },
      labels: {
        style: {
          colors: theme.palette.text.secondary
        }
      },
      min: 0,
      max: 100
    },
    colors: ["#1976d2"],
    title: {
      text: "Consumo de CPU",
      align: "center",
      style: {
        fontSize: "16px",
        fontWeight: 600,
        color: theme.palette.text.primary
      }
    },
    theme: {
      mode: theme.palette.type
    },
    noData: {
      text: "Aguardando dados de histórico...",
      align: "center",
      verticalAlign: "middle"
    }
  };

  const cpuHistorySeries = [
    {
      name: "Consumo de CPU",
      data: hasCpuHistory 
        ? cpuHistoryData.map((h) => h.usage)
        : [0]
    }
  ];

  // Gráfico de barras para mensagens
  const messagesOptions = {
    chart: {
      type: "bar",
      height: 350,
      stacked: false
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%"
      }
    },
    dataLabels: {
      enabled: false
    },
    xaxis: {
      categories: ["Texto", "Áudio", "Imagem"],
      labels: {
        style: {
          colors: theme.palette.text.secondary
        }
      }
    },
    yaxis: {
      title: {
        text: "Quantidade",
        style: {
          color: theme.palette.text.primary
        }
      },
      labels: {
        style: {
          colors: theme.palette.text.secondary
        }
      }
    },
    colors: ["#4caf50", "#f44336"],
    legend: {
      position: "top",
      labels: {
        colors: theme.palette.text.primary
      }
    },
    title: {
      text: "Estatísticas de Mensagens",
      align: "center",
      style: {
        color: theme.palette.text.primary
      }
    },
    theme: {
      mode: theme.palette.type
    }
  };

  const messagesSeries = [
    {
      name: "Enviadas",
      data: [
        stats.messages.sent.text,
        stats.messages.sent.audio,
        stats.messages.sent.image
      ]
    },
    {
      name: "Recebidas",
      data: [
        stats.messages.received.text,
        stats.messages.received.audio,
        stats.messages.received.image
      ]
    }
  ];

  return (
    <Box className={classes.root}>
      <Typography variant="h4" gutterBottom>
        Estatísticas do Servidor
      </Typography>

      {/* Abas */}
      <Paper className={classes.paper} style={{ marginBottom: theme.spacing(2) }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Visão Geral" />
          <Tab label="Processos (htop)" />
          <Tab label="PM2 LOG" />
          <Tab label="COMANDOS" />
        </Tabs>
      </Paper>

      {/* Conteúdo das Abas */}
      {currentTab === 0 && (
      <>
      <Paper className={classes.paper}>
        <Typography variant="h6" gutterBottom>
          Estatísticas do Servidor
        </Typography>
        <Grid container spacing={2} style={{ alignItems: "stretch" }}>
          {/* Gauge de Memória */}
          <Grid item xs={12} md={4} style={{ display: "flex" }}>
            <Paper className={classes.chartContainer} style={{ height: "100%", minHeight: 350, padding: theme.spacing(2), display: "flex", flexDirection: "column", width: "100%" }}>
              <Typography variant="h6" gutterBottom style={{ marginBottom: theme.spacing(1), textAlign: "center" }}>
                Memória (RAM)
              </Typography>
              <Box style={{ width: "100%", height: 280, position: "relative", flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <SafeReactApexChart
                  options={sanitizeApexOptions(memoryGaugeOptions)}
                  series={memoryGaugeSeries}
                  type="radialBar"
                  height={280}
                  width="100%"
                />
              </Box>
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 4 }}>
                  Total: <strong>{formatBytes(stats.server.memory.total)}</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 4 }}>
                  Livre: <strong style={{ color: "#4caf50" }}>{formatBytes(stats.server.memory.free)}</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Usado: <strong style={{ color: "#ff9800" }}>{formatBytes(stats.server.memory.used)}</strong>
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Gauge de CPU */}
          <Grid item xs={12} md={4} style={{ display: "flex" }}>
            <Paper className={classes.chartContainer} style={{ height: "100%", minHeight: 350, padding: theme.spacing(2), display: "flex", flexDirection: "column", width: "100%" }}>
              <Typography variant="h6" gutterBottom style={{ marginBottom: theme.spacing(1), textAlign: "center" }}>
                Processador (CPU)
              </Typography>
              <Box style={{ width: "100%", height: 280, position: "relative", flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <SafeReactApexChart
                  options={sanitizeApexOptions(cpuGaugeOptions)}
                  series={cpuGaugeSeries}
                  type="radialBar"
                  height={280}
                  width="100%"
                />
              </Box>
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary">
                  Núcleos: <strong>{stats.server.cpu.cores}</strong>
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Disco */}
          <Grid item xs={12} md={4} style={{ display: "flex" }}>
            <Paper className={classes.chartContainer} style={{ height: "100%", minHeight: 350, padding: theme.spacing(2), display: "flex", flexDirection: "column", width: "100%" }}>
              <Typography variant="h6" gutterBottom style={{ marginBottom: theme.spacing(1), textAlign: "center" }}>
                HD - Total | Livre
              </Typography>
              <Box style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <Box mb={2} style={{ display: "flex", flexDirection: "row", gap: theme.spacing(2), flexWrap: "wrap", justifyContent: "center" }}>
                  <Box>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 4 }}>
                      Total:
                    </Typography>
                    <Typography variant="body1" style={{ fontWeight: 600 }}>
                      {formatBytes(stats.server.disk.total)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 4 }}>
                      Livre:
                    </Typography>
                    <Typography variant="body1" style={{ fontWeight: 600, color: "#4caf50" }}>
                      {formatBytes(stats.server.disk.free)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 4 }}>
                      Usado:
                    </Typography>
                    <Typography variant="body1" style={{ fontWeight: 600, color: "#ff9800" }}>
                      {formatBytes(stats.server.disk.used)}
                    </Typography>
                  </Box>
                </Box>
                <Box mt={2}>
                  <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, marginBottom: theme.spacing(1), textAlign: "center" }}>
                    HD Usado %
                  </Typography>
                  <Box
                  style={{
                    width: "100%",
                    height: 35,
                    backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[800] : "#e0e0e0",
                    borderRadius: 8,
                    overflow: "hidden",
                    position: "relative",
                    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.1)"
                  }}
                  >
                    <Box
                      style={{
                        width: `${Math.min(stats.server.disk.usedPercent, 100)}%`,
                        height: "100%",
                        backgroundColor:
                          stats.server.disk.usedPercent > 80
                            ? "#f44336"
                            : stats.server.disk.usedPercent > 60
                            ? "#ff9800"
                            : "#4caf50",
                        transition: "width 0.3s ease",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        paddingLeft: 8
                      }}
                    >
                      {stats.server.disk.usedPercent > 15 && (
                        <Typography
                          variant="body2"
                          style={{
                            fontWeight: "bold",
                            color: "#fff",
                            textShadow: "0 1px 2px rgba(0,0,0,0.2)"
                          }}
                        >
                          {stats.server.disk.usedPercent.toFixed(1)}%
                        </Typography>
                      )}
                    </Box>
                    {stats.server.disk.usedPercent <= 15 && (
                      <Typography
                        variant="body2"
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          fontWeight: "bold",
                          color: theme.palette.type === 'dark' ? theme.palette.text.primary : "#666"
                        }}
                      >
                        {stats.server.disk.usedPercent.toFixed(1)}%
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Gráfico de histórico de CPU */}
          <Grid item xs={12}>
            <Paper className={classes.chartContainer} style={{ minHeight: 400 }}>
              {hasCpuHistory ? (
                <SafeReactApexChart
                  options={sanitizeApexOptions(cpuHistoryOptions)}
                  series={cpuHistorySeries}
                  type="line"
                  height={350}
                />
              ) : (
                <Box style={{ 
                  display: "flex", 
                  flexDirection: "column", 
                  alignItems: "center", 
                  justifyContent: "center", 
                  minHeight: 350,
                  padding: theme.spacing(3)
                }}>
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    Consumo de CPU
                  </Typography>
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: theme.spacing(2) }}>
                    ⏳ Aguardando dados de histórico...
                  </Typography>
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: theme.spacing(1), fontSize: "0.75rem" }}>
                    O gráfico será exibido após algumas atualizações (a cada 30 segundos)
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Estatísticas das Empresas */}
      <Paper className={classes.paper}>
        <Typography variant="h6" gutterBottom>
          Estatísticas das Empresas
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Paper className={classes.statCard} style={{ backgroundColor: "#4caf50" }}>
              <Typography className={classes.statLabel} style={{ color: "#fff" }}>
                Total Conexões
              </Typography>
              <Typography className={classes.statValue} style={{ color: "#fff" }}>
                {stats.companies.connections.total}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper className={classes.statCard} style={{ backgroundColor: "#4caf50" }}>
              <Typography className={classes.statLabel} style={{ color: "#fff" }}>
                Conexões Ativas
              </Typography>
              <Typography className={classes.statValue} style={{ color: "#fff" }}>
                {stats.companies.connections.active}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper className={classes.statCard} style={{ backgroundColor: "#f44336" }}>
              <Typography className={classes.statLabel} style={{ color: "#fff" }}>
                Conexões +10 dias
              </Typography>
              <Typography className={classes.statValue} style={{ color: "#fff" }}>
                {stats.companies.connections.inactive}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper className={classes.statCard} style={{ backgroundColor: "#2196f3" }}>
              <Typography className={classes.statLabel} style={{ color: "#fff" }}>
                Total de Usuários
              </Typography>
              <Typography className={classes.statValue} style={{ color: "#fff" }}>
                {stats.users.total}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper className={classes.statCard} style={{ backgroundColor: "#2196f3" }}>
              <Typography className={classes.statLabel} style={{ color: "#fff" }}>
                Usuários Online *
              </Typography>
              <Typography className={classes.statValue} style={{ color: "#fff" }}>
                {stats.users.online}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper className={classes.statCard} style={{ backgroundColor: "#9c27b0" }}>
              <Typography className={classes.statLabel} style={{ color: "#fff" }}>
                Total de Filas
              </Typography>
              <Typography className={classes.statValue} style={{ color: "#fff" }}>
                {stats.queues.total}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper className={classes.statCard} style={{ backgroundColor: "#ff9800" }}>
              <Typography className={classes.statLabel} style={{ color: "#fff" }}>
                Campanhas Ativas
              </Typography>
              <Typography className={classes.statValue} style={{ color: "#fff" }}>
                {stats.campaigns.active}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper className={classes.statCard} style={{ backgroundColor: "#ff9800" }}>
              <Typography className={classes.statLabel} style={{ color: "#fff" }}>
                Campanhas Concluídas
              </Typography>
              <Typography className={classes.statValue} style={{ color: "#fff" }}>
                {stats.campaigns.completed}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Empresas Cadastradas */}
      <Paper className={classes.paper}>
        <Typography variant="h6" gutterBottom>
          Empresas Cadastradas
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Paper className={classes.statCard} style={{ backgroundColor: "#ffc107" }}>
              <Typography className={classes.statLabel} style={{ color: "#000" }}>
                Total
              </Typography>
              <Typography className={classes.statValue} style={{ color: "#000" }}>
                {stats.companies.total}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper className={classes.statCard} style={{ backgroundColor: "#f44336" }}>
              <Typography className={classes.statLabel} style={{ color: "#fff" }}>
                Emp Ativas (Tickets +15d)
              </Typography>
              <Typography className={classes.statValue} style={{ color: "#fff" }}>
                {stats.companies.active}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Gráfico de Mensagens */}
      <Paper className={classes.paper}>
        <SafeReactApexChart
          options={sanitizeApexOptions(messagesOptions)}
          series={messagesSeries}
          type="bar"
          height={350}
        />
      </Paper>

      {/* Empresas com Conexões */}
      <Paper className={classes.paper}>
        <Typography variant="h6" gutterBottom>
          Empresas com Conexões - Geral
        </Typography>
        <Box overflow="auto">
          <table className={classes.table} style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ 
                backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[800] : "#f5f5f5", 
                borderBottom: `2px solid ${theme.palette.type === 'dark' ? theme.palette.grey[700] : "#ddd"}` 
              }}>
                <th style={{ 
                  padding: "12px", 
                  textAlign: "left", 
                  fontWeight: 600,
                  color: theme.palette.text.primary
                }}>Nome</th>
                <th style={{ 
                  padding: "12px", 
                  textAlign: "left", 
                  fontWeight: 600,
                  color: theme.palette.text.primary
                }}>Telefone</th>
                <th style={{ 
                  padding: "12px", 
                  textAlign: "left", 
                  fontWeight: 600,
                  color: theme.palette.text.primary
                }}>Último Login</th>
                <th style={{ 
                  padding: "12px", 
                  textAlign: "left", 
                  fontWeight: 600,
                  color: theme.palette.text.primary
                }}>Status</th>
                <th style={{ 
                  padding: "12px", 
                  textAlign: "left", 
                  fontWeight: 600,
                  color: theme.palette.text.primary
                }}>ID Conexão</th>
              </tr>
            </thead>
            <tbody>
              {stats.companiesWithConnections.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ 
                    padding: "16px", 
                    textAlign: "center", 
                    color: theme.palette.text.secondary 
                  }}>
                    Nenhuma empresa com conexões encontrada
                  </td>
                </tr>
              ) : (
                stats.companiesWithConnections.map((company, index) => (
                  <tr 
                    key={company.id} 
                    style={{ 
                      borderBottom: `1px solid ${theme.palette.type === 'dark' ? theme.palette.grey[700] : "#eee"}`,
                      backgroundColor: index % 2 === 0 
                        ? (theme.palette.type === 'dark' ? theme.palette.background.paper : "#fff")
                        : (theme.palette.type === 'dark' ? theme.palette.grey[900] : "#fafafa")
                    }}
                  >
                    <td style={{ padding: "12px" }}>
                      <Typography variant="body2" style={{ fontWeight: 500, color: theme.palette.text.primary }}>
                        {company.name || "-"}
                      </Typography>
                    </td>
                    <td style={{ padding: "12px" }}>
                      <Typography variant="body2" style={{ color: theme.palette.text.primary }}>
                        {company.phone ? formatPhoneNumber(company.phone) : "-"}
                      </Typography>
                    </td>
                    <td style={{ padding: "12px" }}>
                      <Typography variant="body2" style={{ color: theme.palette.text.primary }}>
                        {company.lastLogin
                          ? new Date(company.lastLogin).toLocaleString("pt-BR", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit"
                            })
                          : "-"}
                      </Typography>
                    </td>
                    <td style={{ padding: "12px" }}>
                      <span
                        style={{
                          color: company.status === "CONNECTED" ? "#4caf50" : "#f44336",
                          fontWeight: "bold",
                          fontSize: "0.875rem",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          backgroundColor: company.status === "CONNECTED" ? "#e8f5e9" : "#ffebee"
                        }}
                      >
                        {company.status}
                      </span>
                    </td>
                    <td style={{ padding: "12px" }}>
                      <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                        {company.connectionId || "-"}
                      </Typography>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Box>
      </Paper>
      </>
      )}

      {currentTab === 1 && (
        <Paper className={classes.paper}>
          <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: theme.spacing(2) }}>
            <Typography variant="h6">
              Processos do Servidor
            </Typography>
            <Box>
              <Button
                variant="outlined"
                color="primary"
                onClick={loadProcesses}
                disabled={loadingProcesses}
                style={{ marginRight: theme.spacing(1) }}
              >
                {loadingProcesses ? "Carregando..." : "Atualizar"}
              </Button>
              <Button
                variant={autoRefresh ? "contained" : "outlined"}
                color={autoRefresh ? "secondary" : "primary"}
                onClick={() => setAutoRefresh(!autoRefresh)}
              >
                {autoRefresh ? "Parar Auto-Refresh" : "Iniciar Auto-Refresh"}
              </Button>
            </Box>
          </Box>

          {loadingProcesses && processes.length === 0 ? (
            <Box className={classes.loading}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow style={{ backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[800] : "#f5f5f5" }}>
                    <TableCell style={{ fontWeight: 600, color: theme.palette.text.primary }}>PID</TableCell>
                    <TableCell style={{ fontWeight: 600, color: theme.palette.text.primary }}>Nome</TableCell>
                    <TableCell style={{ fontWeight: 600, color: theme.palette.text.primary }}>CPU %</TableCell>
                    <TableCell style={{ fontWeight: 600, color: theme.palette.text.primary }}>Memória</TableCell>
                    <TableCell style={{ fontWeight: 600, color: theme.palette.text.primary }}>Memória %</TableCell>
                    <TableCell style={{ fontWeight: 600, color: theme.palette.text.primary }}>Usuário</TableCell>
                    <TableCell style={{ fontWeight: 600, color: theme.palette.text.primary }}>Comando</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {processes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} style={{ textAlign: "center", color: theme.palette.text.secondary }}>
                        Nenhum processo encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    processes.map((process, index) => (
                      <TableRow 
                        key={`${process.pid}-${index}`}
                        style={{ 
                          backgroundColor: index % 2 === 0 
                            ? (theme.palette.type === 'dark' ? theme.palette.background.paper : "#fff")
                            : (theme.palette.type === 'dark' ? theme.palette.grey[900] : "#fafafa")
                        }}
                      >
                        <TableCell style={{ color: theme.palette.text.primary, fontFamily: "monospace" }}>
                          {process.pid}
                        </TableCell>
                        <TableCell style={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                          {process.name}
                        </TableCell>
                        <TableCell style={{ color: theme.palette.text.primary }}>
                          {process.cpu > 0 ? `${process.cpu.toFixed(1)}%` : "N/A"}
                        </TableCell>
                        <TableCell style={{ color: theme.palette.text.primary }}>
                          {process.memory.toFixed(2)} MB
                        </TableCell>
                        <TableCell style={{ color: theme.palette.text.primary }}>
                          {process.memoryPercent.toFixed(2)}%
                        </TableCell>
                        <TableCell style={{ color: theme.palette.text.primary }}>
                          {process.user || "N/A"}
                        </TableCell>
                        <TableCell style={{ color: theme.palette.text.primary, fontFamily: "monospace", fontSize: "0.75rem" }}>
                          {process.command || process.name}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {currentTab === 2 && (
        <Paper className={classes.paper}>
          <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: theme.spacing(2) }}>
            <Typography variant="h6">
              PM2 Logs
            </Typography>
            <Box>
              <Button
                variant="outlined"
                color="primary"
                onClick={loadPm2Logs}
                disabled={loadingPm2Logs}
                style={{ marginRight: theme.spacing(1) }}
              >
                {loadingPm2Logs ? "Carregando..." : "Atualizar"}
              </Button>
              <Button
                variant={autoRefreshLogs ? "contained" : "outlined"}
                color={autoRefreshLogs ? "secondary" : "primary"}
                onClick={() => setAutoRefreshLogs(!autoRefreshLogs)}
              >
                {autoRefreshLogs ? "Parar Auto-Refresh" : "Iniciar Auto-Refresh"}
              </Button>
            </Box>
          </Box>

          {loadingPm2Logs && pm2Logs.length === 0 ? (
            <Box className={classes.loading}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              style={{
                backgroundColor: theme.palette.type === 'dark' ? "#1e1e1e" : "#000",
                color: "#00ff00",
                fontFamily: "monospace",
                fontSize: "0.875rem",
                padding: theme.spacing(2),
                borderRadius: 4,
                maxHeight: "70vh",
                overflow: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word"
              }}
            >
              {pm2Logs.length === 0 ? (
                <Typography style={{ color: theme.palette.text.secondary }}>
                  Nenhum log disponível. Clique em "Atualizar" para carregar os logs do PM2.
                </Typography>
              ) : (
                pm2Logs.map((log, index) => (
                  <div
                    key={index}
                    style={{
                      color: log.includes("ERR") || log.includes("Error") || log.includes("error") 
                        ? "#ff4444" 
                        : log.includes("WARN") || log.includes("Warning") || log.includes("warning")
                        ? "#ffaa00"
                        : "#00ff00",
                      marginBottom: 2,
                      lineHeight: 1.5
                    }}
                  >
                    {log}
                  </div>
                ))
              )}
            </Box>
          )}
        </Paper>
      )}

      {currentTab === 3 && (
        <Paper className={classes.paper}>
          <Typography variant="h6" gutterBottom>
            Comandos do Servidor
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: theme.spacing(3) }}>
            Execute comandos administrativos no servidor. Todos os comandos requerem confirmação.
          </Typography>

          {/* Configuração de Senha Root */}
          <Paper style={{ padding: theme.spacing(2), marginBottom: theme.spacing(3), backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100] }}>
            <Typography variant="h6" gutterBottom>
              🔐 Configuração de Senha Root
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: theme.spacing(2) }}>
              Configure a senha root do servidor para executar comandos que requerem privilégios administrativos (Atualizar Servidor e Reiniciar Servidor).
              {rootPasswordConfigured && (
                <span style={{ color: "#4caf50", fontWeight: "bold", marginLeft: 8 }}>
                  ✓ Senha configurada
                </span>
              )}
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8}>
                <TextField
                  type="password"
                  label="Senha Root"
                  variant="outlined"
                  fullWidth
                  value={rootPassword}
                  onChange={(e) => setRootPassword(e.target.value)}
                  placeholder="Digite a senha root do servidor"
                  disabled={savingRootPassword}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleSaveRootPassword}
                  disabled={savingRootPassword || !rootPassword.trim()}
                >
                  {savingRootPassword ? "Salvando..." : rootPasswordConfigured ? "Atualizar Senha" : "Salvar Senha"}
                </Button>
              </Grid>
            </Grid>
            {!rootPasswordConfigured && (
              <Typography variant="body2" color="error" style={{ marginTop: theme.spacing(1) }}>
                ⚠️ A senha root não está configurada. Os comandos de atualizar e reiniciar servidor podem falhar.
              </Typography>
            )}
          </Paper>

          <Grid container spacing={3}>
            {/* Reiniciar PM2 */}
            <Grid item xs={12} md={4}>
              <Paper style={{ padding: theme.spacing(2), height: "100%" }}>
                <Box style={{ display: "flex", alignItems: "center", marginBottom: theme.spacing(1) }}>
                  <Typography variant="h6" style={{ flex: 1 }}>
                    Reiniciar PM2
                  </Typography>
                  <Box 
                    display="flex" 
                    alignItems="center" 
                    style={{ cursor: "pointer", color: "#f50057" }}
                    onClick={handleHelpClick("restartPm2")}
                  >
                    <HelpOutlineIcon style={{ marginRight: 4 }} />
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      Ajuda
                    </Typography>
                  </Box>
                  <Popover
                    open={Boolean(helpAnchorEl.restartPm2)}
                    anchorEl={helpAnchorEl.restartPm2}
                    onClose={() => handleHelpClose("restartPm2")}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    PaperProps={{
                      style: {
                        maxWidth: 450,
                        padding: 20,
                        borderRadius: 8,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.15)"
                      }
                    }}
                  >
                    <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 12, color: "#1976d2" }}>
                      🔄 Reiniciar PM2
                    </Typography>
                    <Box style={{ 
                      backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[800] : "#f5f5f5", 
                      borderRadius: 8, 
                      padding: 12,
                      marginBottom: 8
                    }}>
                      <Typography variant="body2" component="div" style={{ lineHeight: 2, color: theme.palette.text.primary }}>
                        Use esse recurso, caso o sistema estiver lento, ou as conexões estiverem travadas.
                        <br />
                        <br />
                        Este comando reinicia todos os processos gerenciados pelo PM2, o que pode resolver problemas de performance e conexões travadas.
                      </Typography>
                    </Box>
                  </Popover>
                </Box>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: theme.spacing(2) }}>
                  Reinicia todos os processos do PM2
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() => handleExecuteCommand("restart-pm2")}
                >
                  Reiniciar PM2
                </Button>
              </Paper>
            </Grid>

            {/* Reiniciar Servidor */}
            <Grid item xs={12} md={4}>
              <Paper style={{ padding: theme.spacing(2), height: "100%" }}>
                <Box style={{ display: "flex", alignItems: "center", marginBottom: theme.spacing(1) }}>
                  <Typography variant="h6" style={{ flex: 1 }}>
                    Reiniciar Servidor
                  </Typography>
                  <Box 
                    display="flex" 
                    alignItems="center" 
                    style={{ cursor: "pointer", color: "#f50057" }}
                    onClick={handleHelpClick("restartServer")}
                  >
                    <HelpOutlineIcon style={{ marginRight: 4 }} />
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      Ajuda
                    </Typography>
                  </Box>
                  <Popover
                    open={Boolean(helpAnchorEl.restartServer)}
                    anchorEl={helpAnchorEl.restartServer}
                    onClose={() => handleHelpClose("restartServer")}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    PaperProps={{
                      style: {
                        maxWidth: 450,
                        padding: 20,
                        borderRadius: 8,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.15)"
                      }
                    }}
                  >
                    <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 12, color: "#1976d2" }}>
                      ⚠️ Reiniciar Servidor
                    </Typography>
                    <Box style={{ 
                      backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[800] : "#f5f5f5", 
                      borderRadius: 8, 
                      padding: 12,
                      marginBottom: 8
                    }}>
                      <Typography variant="body2" component="div" style={{ lineHeight: 2, color: theme.palette.text.primary }}>
                        <strong>Atenção:</strong> Esse comando vai reiniciar o servidor, ideal para limpar memória, e deixar o sistema mais rápido.
                        <br />
                        <br />
                        <strong style={{ color: "#f44336" }}>⚠️ IMPORTANTE:</strong>
                        <br />
                        • O sistema ficará indisponível, até que o servidor seja reiniciado, inclusive este painel.
                        <br />
                        • O tempo médio de downtime pode variar de 1 a 2 minutos dependendo do servidor.
                        <br />
                        • Certifique-se de que não há operações críticas em andamento antes de executar este comando.
                      </Typography>
                    </Box>
                  </Popover>
                </Box>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: theme.spacing(2) }}>
                  Reinicia o servidor completamente
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  onClick={() => handleExecuteCommand("restart-server")}
                >
                  Reiniciar Servidor
                </Button>
              </Paper>
            </Grid>

            {/* Atualizar Servidor */}
            <Grid item xs={12} md={4}>
              <Paper style={{ padding: theme.spacing(2), height: "100%" }}>
                <Box style={{ display: "flex", alignItems: "center", marginBottom: theme.spacing(1) }}>
                  <Typography variant="h6" style={{ flex: 1 }}>
                    Atualizar Servidor
                  </Typography>
                  <Box 
                    display="flex" 
                    alignItems="center" 
                    style={{ cursor: "pointer", color: "#f50057" }}
                    onClick={handleHelpClick("updateServer")}
                  >
                    <HelpOutlineIcon style={{ marginRight: 4 }} />
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      Ajuda
                    </Typography>
                  </Box>
                  <Popover
                    open={Boolean(helpAnchorEl.updateServer)}
                    anchorEl={helpAnchorEl.updateServer}
                    onClose={() => handleHelpClose("updateServer")}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    PaperProps={{
                      style: {
                        maxWidth: 450,
                        padding: 20,
                        borderRadius: 8,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.15)"
                      }
                    }}
                  >
                    <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 12, color: "#1976d2" }}>
                      📦 Atualizar Servidor
                    </Typography>
                    <Box style={{ 
                      backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[800] : "#f5f5f5", 
                      borderRadius: 8, 
                      padding: 12,
                      marginBottom: 8
                    }}>
                      <Typography variant="body2" component="div" style={{ lineHeight: 2, color: theme.palette.text.primary }}>
                        Esse comando atualiza os pacotes do servidor, deixando o sistema operacional sempre atualizado.
                        <br />

                        <br />
                        <strong>Comandos executados:</strong>
                        <br />
                        • <code>apt update -y</code> - Atualiza a lista de pacotes disponíveis
                        <br />
                        • <code>apt upgrade -y</code> - Atualiza os pacotes instalados
                        <br />
                        <br />
                        <strong style={{ color: "#ff9800" }}>💡 Dica:</strong> Depois de executar, é recomendado reiniciar o servidor para aplicar todas as atualizações.
                      </Typography>
                    </Box>
                  </Popover>
                </Box>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: theme.spacing(2) }}>
                  Atualiza os pacotes do sistema
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() => handleExecuteCommand("update-server")}
                >
                  Atualizar
                </Button>
              </Paper>
            </Grid>

            {/* Atualizar Sistema */}
            <Grid item xs={12} md={4}>
              <Paper style={{ padding: theme.spacing(2), height: "100%" }}>
                <Box style={{ display: "flex", alignItems: "center", marginBottom: theme.spacing(1) }}>
                  <Typography variant="h6" style={{ flex: 1 }}>
                    Atualizar Sistema
                  </Typography>
                  <Box 
                    display="flex" 
                    alignItems="center" 
                    style={{ cursor: "pointer", color: "#f50057" }}
                    onClick={handleHelpClick("updateSystem")}
                  >
                    <HelpOutlineIcon style={{ marginRight: 4 }} />
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      Ajuda
                    </Typography>
                  </Box>
                  <Popover
                    open={Boolean(helpAnchorEl.updateSystem)}
                    anchorEl={helpAnchorEl.updateSystem}
                    onClose={() => handleHelpClose("updateSystem")}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    PaperProps={{
                      style: {
                        maxWidth: 450,
                        padding: 20,
                        borderRadius: 8,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.15)"
                      }
                    }}
                  >
                    <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 12, color: "#1976d2" }}>
                      🔄 Atualizar Sistema
                    </Typography>
                    <Box style={{ 
                      backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[800] : "#f5f5f5", 
                      borderRadius: 8, 
                      padding: 12,
                      marginBottom: 8
                    }}>
                      <Typography variant="body2" component="div" style={{ lineHeight: 2, color: theme.palette.text.primary }}>
                        <strong>Atenção:</strong> Esse comando vai atualizar o sistema para a última versão disponível.
                        <br />
                        <br />
                        <strong style={{ color: "#f44336" }}>⚠️ IMPORTANTE:</strong>
                        <br />
                        • O sistema pode ficar instável por até 20 minutos durante a atualização.
                        <br />
                        • É recomendado que esse processo seja feito fora de expediente, para evitar transtornos.
                        <br />
                        • O processo pode levar até 20 minutos para concluir.
                      </Typography>
                    </Box>
                  </Popover>
                </Box>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: theme.spacing(2) }}>
                  Atualiza o sistema para a última versão disponível
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() => handleExecuteCommand("update-system")}
                  style={{ marginBottom: theme.spacing(1) }}
                >
                  Atualizar Sistema
                </Button>
                {/* Botão de TESTE - Apenas para desenvolvimento local */}
                {process.env.NODE_ENV === 'development' && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    fullWidth
                    onClick={handleSimulateUpdate}
                    style={{ marginTop: theme.spacing(1) }}
                  >
                    🧪 Testar (Simular Atualização)
                  </Button>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Dialog de Confirmação */}
      <Dialog
        open={commandDialog.open}
        onClose={() => !executingCommand && setCommandDialog({ open: false, type: null, confirmation: "" })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {commandDialog.type === "restart-pm2" && "Confirmar Reiniciar PM2"}
          {commandDialog.type === "restart-server" && "Confirmar Reiniciar Servidor"}
          {commandDialog.type === "update-server" && "Confirmar Atualizar Servidor"}
          {commandDialog.type === "update-system" && "Confirmar Atualizar Sistema"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: theme.spacing(2) }}>
            {commandDialog.type === "restart-pm2" && "Digite 'REINICIAR PM2' para confirmar:"}
            {commandDialog.type === "restart-server" && (
              <>
                ⚠️ <strong>ATENÇÃO:</strong> O servidor será reiniciado.
                <br />
                <br />
                O sistema ficará indisponível, até que o servidor seja reiniciado, inclusive este painel.
                <br />
                O tempo médio de downtime pode variar de 1 a 2 minutos dependendo do servidor.
                <br />
                <br />
                Digite 'REINICIAR SERVIDOR' para confirmar:
              </>
            )}
            {commandDialog.type === "update-server" && "Digite 'ATUALIZAR' para confirmar:"}
            {commandDialog.type === "update-system" && (
              <>
                ⚠️ <strong>ATENÇÃO:</strong> O sistema será atualizado para a última versão disponível.
                <br />
                <br />
                O sistema pode ficar instável por até 20 minutos durante a atualização.
                <br />
                É recomendado que esse processo seja feito fora de expediente, para evitar transtornos.
                <br />
                <br />
                Digite 'ATUALIZAR SISTEMA' para confirmar:
              </>
            )}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Confirmação"
            type="text"
            fullWidth
            value={commandDialog.confirmation}
            onChange={(e) => setCommandDialog({ ...commandDialog, confirmation: e.target.value })}
            disabled={executingCommand}
            placeholder={
              commandDialog.type === "restart-pm2" ? "REINICIAR PM2" :
              commandDialog.type === "restart-server" ? "REINICIAR SERVIDOR" :
              commandDialog.type === "update-system" ? "ATUALIZAR SISTEMA" :
              "ATUALIZAR"
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCommandDialog({ open: false, type: null, confirmation: "" })}
            disabled={executingCommand}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmCommand}
            color="primary"
            variant="contained"
            disabled={executingCommand || !commandDialog.confirmation}
          >
            {executingCommand ? "Executando..." : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Progresso da Atualização do Sistema */}
      {user && (
        <UpdateSystemProgress
          open={updateSystemProgress.open}
          onClose={() => setUpdateSystemProgress(prev => ({ ...prev, open: false }))}
          companyId={user.companyId}
          userId={user.id}
        />
      )}
    </Box>
  );
};

export default ServerDashboard;
